import React, { useState } from 'react';
import {
    Show,
    SimpleShowLayout,
    useRecordContext,
    useRedirect,
    useTranslate,
    ReferenceManyField,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    Pagination,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Tabs,
    Tab,
    Divider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ArrowBack,
    CreditCard,
    Person,
    LocationCity,
    Settings,
    History,
    CheckCircle,
    Cancel,
    Business,
} from '@mui/icons-material';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`rfid-card-tabpanel-${index}`}
            aria-labelledby={`rfid-card-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: { xs: 2, sm: 3 } }}>{children}</Box>}
        </div>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    const record = useRecordContext();

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, sm: 3 } }}>
            <IconButton
                onClick={() => redirect('/rfid-cards')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1.25rem', sm: '2.125rem' },
                        mb: 0.5,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {translate('resources.rfid-cards.show.title')}
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                >
                    {record?.cardNumber || ''}
                </Typography>
            </Box>
        </Box>
    );
};

const SectionHeader = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {React.cloneElement(icon as React.ReactElement<any>, {
            sx: { mr: 2, color: '#00C853' },
        })}
        <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}
        >
            {title}
        </Typography>
    </Box>
);

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 2 }, py: 1 }}>
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: { sm: 180 } }}
        >
            {label}
        </Typography>
        <Box sx={{ flex: 1 }}>
            {typeof value === 'string' || typeof value === 'number' ? (
                <Typography variant="body2">{value}</Typography>
            ) : (
                value
            )}
        </Box>
    </Box>
);

const CardInfoSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <SectionHeader
                    icon={<CreditCard />}
                    title={translate('resources.rfid-cards.sections.cardInfo')}
                />
                <InfoRow
                    label={translate('resources.rfid-cards.fields.cardNumber')}
                    value={
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {record.cardNumber}
                        </Typography>
                    }
                />
                <Divider />
                <InfoRow
                    label={translate('resources.rfid-cards.fields.cardType')}
                    value={
                        <Chip
                            label={record.cardType || 'STANDARD'}
                            size="small"
                            color={record.cardType === 'ADMIN' ? 'secondary' : 'default'}
                        />
                    }
                />
                <Divider />
                <InfoRow
                    label={translate('resources.rfid-cards.fields.id')}
                    value={
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {record.id}
                        </Typography>
                    }
                />
            </CardContent>
        </Card>
    );
};

const AssignmentSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <SectionHeader
                    icon={<Person />}
                    title={translate('resources.rfid-cards.sections.assignment')}
                />
                <InfoRow
                    label={translate('resources.rfid-cards.fields.vendor')}
                    value={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business sx={{ fontSize: 18, color: '#666' }} />
                            <Typography variant="body2">{record.vendorName || 'N/A'}</Typography>
                        </Box>
                    }
                />
                <Divider />
                <InfoRow
                    label={translate('resources.rfid-cards.fields.user')}
                    value={
                        <Box>
                            <Typography variant="body2">{record.userName || 'N/A'}</Typography>
                            {record.userEmail && (
                                <Typography variant="caption" color="text.secondary">
                                    {record.userEmail}
                                </Typography>
                            )}
                        </Box>
                    }
                />
            </CardContent>
        </Card>
    );
};

const SiteAccessSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;

    const sites = record.sites || [];

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <SectionHeader
                    icon={<LocationCity />}
                    title={translate('resources.rfid-cards.sections.siteAccess')}
                />
                {sites.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {translate('resources.rfid-cards.edit.noSitesAvailable')}
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {sites.map((site: any) => (
                            <Chip
                                key={site.siteId}
                                label={site.siteName || site.siteId}
                                size="small"
                                sx={{
                                    backgroundColor: '#E8F5E9',
                                    color: '#1B5E20',
                                }}
                            />
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const SettingsSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <SectionHeader
                    icon={<Settings />}
                    title={translate('resources.rfid-cards.sections.settings')}
                />
                <InfoRow
                    label={translate('resources.rfid-cards.fields.isActive')}
                    value={
                        <Chip
                            icon={record.isActive ? <CheckCircle /> : <Cancel />}
                            label={
                                record.isActive
                                    ? translate('resources.rfid-cards.status.active')
                                    : translate('resources.rfid-cards.status.suspended')
                            }
                            color={record.isActive ? 'success' : 'default'}
                            size="small"
                        />
                    }
                />
                <Divider />
                <InfoRow
                    label={translate('resources.rfid-cards.fields.usageCount')}
                    value={record.currentUses || 0}
                />
                {record.lastUsedAt && (
                    <>
                        <Divider />
                        <InfoRow
                            label={translate('resources.rfid-cards.fields.lastUsed')}
                            value={
                                <DateField source="lastUsedAt" record={record} showTime />
                            }
                        />
                    </>
                )}
                <Divider />
                <InfoRow
                    label={translate('resources.rfid-cards.fields.createdAt')}
                    value={<DateField source="createdAt" record={record} showTime />}
                />
            </CardContent>
        </Card>
    );
};

const ChargingHistorySection = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <SectionHeader
                    icon={<History />}
                    title={translate('resources.rfid-cards.sections.usageHistory')}
                />

                <ReferenceManyField
                    reference="charging-sessions"
                    target="rfid_card_id"
                    perPage={10}
                    sort={{ field: 'session_start_time', order: 'DESC' }}
                    pagination={<Pagination />}
                >
                    <Datagrid
                        rowClick={(id) => `/charging-sessions/${id}/show`}
                        bulkActionButtons={false}
                        empty={
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                {translate('resources.rfid-cards.show.noChargingHistory')}
                            </Typography>
                        }
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <FunctionField
                            label={translate('resources.rfid-cards.show.columns.session')}
                            render={(record: any) => {
                                const sessionId = record.app_session_id || '';
                                const truncated =
                                    sessionId.length > 20
                                        ? `${sessionId.substring(0, 8)}...${sessionId.substring(sessionId.length - 8)}`
                                        : sessionId;
                                return (
                                    <Typography variant="body2" fontFamily="monospace">
                                        {truncated}
                                    </Typography>
                                );
                            }}
                        />
                        <DateField
                            source="session_start_time"
                            label={translate('resources.rfid-cards.show.columns.startTime')}
                            showTime
                        />
                        {!isMobile && (
                            <TextField
                                source="site_name"
                                label={translate('resources.rfid-cards.show.columns.site')}
                            />
                        )}
                        {!isMobile && (
                            <TextField
                                source="charger_name"
                                label={translate('resources.rfid-cards.show.columns.charger')}
                            />
                        )}
                        <FunctionField
                            label={translate('resources.rfid-cards.show.columns.energy')}
                            render={(record: any) => {
                                const kwh = parseFloat(record.energy_delivered_kwh || 0);
                                return (
                                    <Typography variant="body2">{kwh.toFixed(2)} kWh</Typography>
                                );
                            }}
                        />
                        <FunctionField
                            label={translate('resources.rfid-cards.show.columns.cost')}
                            render={(record: any) => {
                                const cost = parseFloat(record.final_cost || 0);
                                const currency = record.currency || '';
                                return (
                                    <Typography variant="body2">
                                        {currency} {cost.toFixed(2)}
                                    </Typography>
                                );
                            }}
                        />
                    </Datagrid>
                </ReferenceManyField>
            </CardContent>
        </Card>
    );
};

const RfidCardDetails = () => {
    const [tabValue, setTabValue] = useState(0);
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const record = useRecordContext();

    if (!record) return null;

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
            <PageHeader />

            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="rfid card details tabs"
                        variant={isMobile ? 'fullWidth' : 'scrollable'}
                        scrollButtons={isMobile ? false : 'auto'}
                        allowScrollButtonsMobile
                        sx={{
                            minHeight: { xs: 40, sm: 48 },
                            '& .MuiTab-root': {
                                minHeight: { xs: 40, sm: 48 },
                                textTransform: 'none',
                            },
                        }}
                    >
                        <Tab
                            label={translate('resources.rfid-cards.show.tabs.details')}
                            icon={isMobile ? undefined : <CreditCard />}
                            iconPosition="start"
                        />
                        <Tab
                            label={translate('resources.rfid-cards.show.tabs.chargingHistory')}
                            icon={isMobile ? undefined : <History />}
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                    <CardInfoSection />
                    <AssignmentSection />
                    <SiteAccessSection />
                    <SettingsSection />
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <ChargingHistorySection />
                </TabPanel>
            </Box>
        </Box>
    );
};

export const RfidCardsShow = () => (
    <Show
        title={false}
        actions={false}
        component="div"
        sx={{
            maxWidth: '100%',
            backgroundColor: 'transparent',
            '& .RaShow-main': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
            },
        }}
    >
        <SimpleShowLayout
            sx={{
                '& .MuiPaper-root': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
                '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                    px: { xs: 0, sm: 2 },
                    py: { xs: 1, sm: 2 },
                },
                padding: { xs: 0, sm: 'inherit' },
            }}
        >
            <RfidCardDetails />
        </SimpleShowLayout>
    </Show>
);

export default RfidCardsShow;
